import { useEffect, useRef } from 'react';
import { corelCommunication } from '../../../lib/corel/communication';
import { ExportEvent } from '../types';
import { debugLog } from '../../../lib/terminalLogger';

/** Normaliza mensagens vindas do WebView para um objeto ExportEvent. */
function normalizeEvent(raw: unknown): ExportEvent {
  let obj: any = raw;
  if (typeof raw === 'string') {
    try { obj = JSON.parse(raw as string); } catch { obj = { message: String(raw) }; }
  }
  return obj as ExportEvent;
}

export interface UseCorelExportChannel {
  post(message: unknown): void;
  subscribe(handler: (evt: ExportEvent) => void): () => void;
}

/**
 * Encapsula setupListener e postMessage do canal com o C# (WebView/Corel).
 */
export function useCorelExportChannel(): UseCorelExportChannel {
  const listenersRef = useRef<Array<(evt: ExportEvent) => void>>([]);

  useEffect(() => {
    const cleanup = corelCommunication.setupListener((response) => {
      const evt = normalizeEvent(response);
      listenersRef.current.forEach(fn => {
        try { fn(evt); } catch (e) { debugLog('ERROR', { scope: 'useCorelExportChannel.listener', errorMessage: String(e).slice(0, 200) }); }
      });
    });
    return () => { cleanup(); listenersRef.current = []; };
  }, []);

  return {
    post: (message: unknown) => {
      // Tentar extrair action e data para usar CorelCommunication.sendMessage
      let payload: unknown = message;
      if (typeof message === 'string') {
        try { payload = JSON.parse(message); } catch { /* manter string se não for JSON */ }
      }
      
      // Se payload tem formato { action, data }, usar sendActionGuarded centralizado
      if (payload && typeof payload === 'object' && 'action' in payload) {
        const msg = payload as any;
        corelCommunication.sendActionGuarded(msg.action, msg.data || {});
      } else {
        // Fallback: logar erro (não deveria acontecer)
        debugLog('ERROR', { 
          scope: 'useCorelExportChannel.post', 
          message: 'Payload sem action definida', 
          payload 
        });
      }
    },
    subscribe: (handler: (evt: ExportEvent) => void) => {
      listenersRef.current.push(handler);
      return () => {
        listenersRef.current = listenersRef.current.filter(h => h !== handler);
      };
    }
  };
}


